import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { PReportPeriod, WeekReportTableItem } from 'shared/api/api';
import { SharedAppState } from 'shared/state/store.shared';
import { GetWeekReport } from 'web/web-helpers/pdf-helper/production-report-helper';
import { useWeekReportContext } from './week-report-overview';
import { WeekReportTableModel, Period } from './week-report-table-model';
import SkioldTableGrid from 'web/view/components/skiold-components/skiold-table/skiold-table-grid/skiold-table-grid';
import { localized } from 'shared/state/i18n/i18n';
import { WhiteText } from 'native/view/components/skiold-components/skiold-text/white-text';
import { FlexColumn } from 'web/view/components/basic-flex/flex-column';
import { TextWeb } from 'web/web-helpers/styled-text-components';
import { getStringDate, getWeekNumber, getWeeksInPeriod } from 'shared/helpers/date-helpers';
import { ClipLoader } from 'react-spinners';

// tmp dates are used in the datepicker as they shouldnt come into effect before the fetch button is clicked
interface PropsFromParent {}

type Props = PropsFromParent;

const inlineHeaders = ['Matings', 'Farrowing', 'Weaning', 'Mortality'];

const decimalHelpers: Record<string, 1 | 2> = {
	DiversePercentDead: 1,
	FeedPerPigDaily: 2,
	FeedPerKgGrowth: 2,
	WeanerReferenceFeedUsage: 2,
	FinisherReferenceFeedUsage: 2,
	FRATSReferenceFeedUsage: 2,
};

export const WeekReportTable: React.FC<Props> = () => {
	const { siteId } = useSelector((state: SharedAppState) => state.profile.active!);
	const { currentRows, setCurrentRows } = useWeekReportContext();
	const [rows, setRows] = React.useState<WeekReportTableItem[]>([]);
	const [headers, setHeaders] = React.useState<PReportPeriod[]>([]);
	const [syncSpinner, setSyncSpinner] = React.useState(false);
	useEffect(() => {
		async function fetchData() {
			if (!siteId) {
				return;
			}
			setSyncSpinner(true);
			const response = await GetWeekReport(siteId, currentRows);
			if (response && response.weekReportTableItems && response.periods) {
				setRows(response.weekReportTableItems);
				setHeaders(response.periods);
			}
			setSyncSpinner(false);
		}
		fetchData();
	}, [currentRows, siteId]);

	const generateColumns = useMemo(() => {
		if (!headers) {
			return [];
		}
		let columns = [
			{
				name: 'Name',
				header: (
					<FlexColumn sx={{ textAlign: 'start', width: '100%', alignItems: 'flex-end' }}>
						<TextWeb> {localized('FromDate')}</TextWeb>
						<TextWeb> {localized('ToDate')}</TextWeb>
						<TextWeb> {localized('Week')}</TextWeb>
					</FlexColumn>
				),
				getCellValue: (d: WeekReportTableItem) => localized(d.name ?? ''),
			},
		] as any[];

		headers.forEach(header => {
			const key = header.periodNumber.toString();
			columns.push({
				name: key,

				header: (
					<FlexColumn sx={{ width: '100%' }}>
						<TextWeb> {getStringDate(header.fromDate?.toString())}</TextWeb>
						<TextWeb> {getStringDate(header.toDate?.toString())}</TextWeb>
						<TextWeb> {getWeeksInPeriod(header.fromDate, header.toDate).join(', ')}</TextWeb>
					</FlexColumn>
				),
				//	header: this.GetHeaderForDashboardTableModelData(data),
				getCellValue: (d: WeekReportTableItem) => {
					return d?.name && inlineHeaders.includes(d?.name)
						? ''
						: d.periods
						? d.periods[key as keyof WeekReportTableItem]
						: undefined;
				},
			});
		});
		return columns;
	}, [headers]);

	const generateColumnsExtensions = useMemo(() => {
		if (!headers) {
			return [];
		}
		let columns = [
			{
				columnName: 'Name',
				width: 200,
			},
		] as any[];

		headers.forEach(header => {
			columns.push({
				columnName: header.periodNumber.toString(),
				width: 80,
			});
		});
		return columns;
	}, [headers]);

	if (syncSpinner) {
		return <ClipLoader color="#f2ac40" size={70} />;
	}

	return (
		<SkioldTableGrid
			className="week-report"
			tableKey="week-report"
			ColumnExtensions={generateColumnsExtensions}
			columns={generateColumns}
			data={rows}
			filterable={false}
			InlineHeaders={inlineHeaders}
		/>
	);
};
